import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWsEvent } from '@/hooks/use-websocket';
import { getActiveTimeEntries, getOcrHealth, getPendingReceipts, getReceiptAnalysisItems, getReceiptAnalysisSummary, } from '@/services/pos-api';
import { toDateStringInTz, VENUE_TIMEZONE } from '@/lib/timezone';
const MAX_STREAM_EVENTS = 25;
function formatCurrency(amount) {
    return `$${Number(amount).toFixed(2)}`;
}
function formatVenueTime(date) {
    return new Date(date).toLocaleTimeString('en-CA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: VENUE_TIMEZONE,
    });
}
function describeOcrHealth(health) {
    if (!health.reachable)
        return health.error || 'OCR Pi unreachable';
    if (health.modelLoaded === false)
        return 'OCR Pi reachable; model is still loading';
    return health.responseTimeMs != null
        ? `OCR Pi online · ${health.responseTimeMs}ms`
        : 'OCR Pi online';
}
function reviewStatusLabel(status) {
    if (status === 'VERIFIED')
        return 'manually verified';
    if (status === 'REJECTED')
        return 'manually rejected';
    return 'manual review cleared';
}
async function safeLoad(label, loader, fallback) {
    try {
        return await loader();
    }
    catch (err) {
        console.error(`[Dashboard] Failed to load ${label}:`, err);
        return fallback;
    }
}
export function useDashboardOperationalSignals({ selectedDate, canViewReceiptOps, isAdmin, bookings, currentTime, }) {
    const mountedRef = useRef(true);
    const [pendingReceipts, setPendingReceipts] = useState([]);
    const [receiptReviewItems, setReceiptReviewItems] = useState([]);
    const [receiptSummary, setReceiptSummary] = useState(null);
    const [ocrHealth, setOcrHealth] = useState(null);
    const [lastOcrProbe, setLastOcrProbe] = useState(null);
    const [activeTimeEntries, setActiveTimeEntries] = useState([]);
    const [attentionReadIds, setAttentionReadIds] = useState(() => new Set());
    const [streamEvents, setStreamEvents] = useState([]);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const appendStreamEvent = useCallback((event) => {
        const timestamp = event.timestamp ?? new Date();
        setStreamEvents((prev) => [
            { ...event, timestamp },
            ...prev.filter((existing) => existing.id !== event.id),
        ].slice(0, MAX_STREAM_EVENTS));
    }, []);
    const loadSignals = useCallback(async () => {
        const [nextPendingReceipts, nextReviewItems, nextReceiptSummary, nextOcrHealth, nextActiveTimeEntries,] = await Promise.all([
            canViewReceiptOps
                ? safeLoad('pending receipts', () => getPendingReceipts(selectedDate), [])
                : Promise.resolve([]),
            isAdmin
                ? safeLoad('receipt reviews', () => getReceiptAnalysisItems({ date: selectedDate, needsReview: true }), [])
                : Promise.resolve([]),
            isAdmin
                ? safeLoad('receipt analysis summary', () => getReceiptAnalysisSummary(selectedDate, selectedDate), null)
                : Promise.resolve(null),
            isAdmin
                ? safeLoad('OCR health', () => getOcrHealth(), {
                    reachable: false,
                    error: 'Health check failed',
                })
                : Promise.resolve(null),
            isAdmin
                ? safeLoad('active time entries', () => getActiveTimeEntries(), [])
                : Promise.resolve([]),
        ]);
        if (!mountedRef.current)
            return;
        setPendingReceipts(nextPendingReceipts);
        setReceiptReviewItems(nextReviewItems);
        setReceiptSummary(nextReceiptSummary);
        setOcrHealth(nextOcrHealth);
        setActiveTimeEntries(nextActiveTimeEntries);
        if (isAdmin)
            setLastOcrProbe(new Date());
    }, [canViewReceiptOps, isAdmin, selectedDate]);
    useEffect(() => {
        loadSignals();
        const handle = window.setInterval(loadSignals, 60000);
        return () => window.clearInterval(handle);
    }, [loadSignals]);
    useWsEvent('receipt.uploaded', (evt) => {
        appendStreamEvent({
            id: `receipt-uploaded-${evt.payload.paymentId}-${evt.timestamp}`,
            type: 'ReceiptUpload',
            primary: 'Receipt uploaded',
            secondary: evt.payload.bookingId ? `Booking ${evt.payload.bookingId.slice(0, 8)}` : undefined,
            meta: evt.payload.paymentId,
            linkHref: evt.payload.bookingId ? `/pos/booking/${evt.payload.bookingId}` : '/admin/receipt-analysis',
        });
        loadSignals();
    });
    useWsEvent('receipt.deleted', (evt) => {
        appendStreamEvent({
            id: `receipt-deleted-${evt.payload.paymentId}-${evt.timestamp}`,
            type: 'ReceiptUpload',
            primary: 'Receipt removed',
            secondary: evt.payload.bookingId ? `Booking ${evt.payload.bookingId.slice(0, 8)}` : undefined,
            meta: evt.payload.paymentId,
            linkHref: evt.payload.bookingId ? `/pos/booking/${evt.payload.bookingId}` : '/admin/receipt-analysis',
        });
        loadSignals();
    });
    useWsEvent('receipt.analysis_complete', (evt) => {
        appendStreamEvent({
            id: `receipt-analysis-${evt.payload.paymentId}-${evt.timestamp}`,
            type: 'ReceiptReview',
            primary: `OCR ${evt.payload.matchStatus || 'analysis complete'}`,
            secondary: evt.payload.bookingId ? `Booking ${evt.payload.bookingId.slice(0, 8)}` : undefined,
            meta: evt.payload.paymentId,
            linkHref: evt.payload.bookingId ? `/pos/booking/${evt.payload.bookingId}` : '/admin/receipt-analysis',
        });
        loadSignals();
    });
    useWsEvent('receipt.analysis_progress', (evt) => {
        appendStreamEvent({
            id: `receipt-analysis-progress-${evt.payload.analysisRunId}-${evt.payload.stage}-${evt.timestamp}`,
            timestamp: new Date(evt.timestamp),
            type: 'OcrAnalyzer',
            level: evt.payload.level,
            primary: evt.payload.message,
            secondary: evt.payload.bookingId ? `Booking ${evt.payload.bookingId.slice(0, 8)}` : undefined,
            meta: [
                evt.payload.paymentId,
                evt.payload.detail,
                evt.payload.elapsedMs != null ? `${evt.payload.elapsedMs}ms` : undefined,
            ].filter(Boolean).join(' · '),
            linkHref: evt.payload.bookingId ? `/pos/booking/${evt.payload.bookingId}` : '/admin/receipt-analysis',
        });
        if (['completed', 'failed', 'skipped'].includes(evt.payload.stage)) {
            loadSignals();
        }
    });
    useWsEvent('receipt.manual_review', (evt) => {
        appendStreamEvent({
            id: `receipt-review-${evt.payload.paymentId}-${evt.timestamp}`,
            type: 'ReceiptReview',
            primary: reviewStatusLabel(evt.payload.manualReviewStatus),
            secondary: evt.payload.bookingId ? `Booking ${evt.payload.bookingId.slice(0, 8)}` : undefined,
            meta: evt.payload.paymentId,
            linkHref: evt.payload.bookingId ? `/pos/booking/${evt.payload.bookingId}` : '/admin/receipt-analysis',
        });
        loadSignals();
    });
    useWsEvent('ocr.pi_health_changed', (evt) => {
        setOcrHealth(evt.payload);
        setLastOcrProbe(new Date(evt.timestamp));
        appendStreamEvent({
            id: `ocr-health-${evt.timestamp}`,
            type: 'OcrHealth',
            primary: describeOcrHealth(evt.payload),
            meta: evt.payload.ocrServiceUrl,
            linkHref: '/admin/receipt-analysis',
        });
    });
    const refreshTimeclockSignals = useCallback((evt) => {
        appendStreamEvent({
            id: `timeclock-${evt.payload.change}-${evt.payload.entryId || evt.payload.employeeId || evt.timestamp}`,
            type: 'StatusChange',
            primary: `Timeclock ${evt.payload.change.replace('_', ' ')}`,
            meta: evt.payload.employeeId,
            linkHref: '/pos/time-management',
        });
        loadSignals();
    }, [appendStreamEvent, loadSignals]);
    useWsEvent('timeclock.clocked_in', refreshTimeclockSignals);
    useWsEvent('timeclock.clocked_out', refreshTimeclockSignals);
    useWsEvent('timeclock.edited', refreshTimeclockSignals);
    const attentionItems = useMemo(() => {
        const now = currentTime.getTime();
        const venueToday = toDateStringInTz(currentTime, VENUE_TIMEZONE);
        const items = [];
        pendingReceipts.forEach((payment) => {
            items.push({
                id: `missing-receipt-${payment.paymentId}`,
                kind: 'missing_receipt',
                severity: 'amber',
                title: `${payment.booking.roomName} · ${formatCurrency(payment.amount)}`,
                detail: `${payment.booking.customerName || 'Walk-in'} · ${payment.method} · paid ${formatVenueTime(payment.createdAt)}`,
                linkHref: `/pos/booking/${payment.booking.id}`,
                subjectId: payment.paymentId,
                createdAt: payment.createdAt,
            });
        });
        if (isAdmin) {
            receiptReviewItems.forEach((item) => {
                const status = item.analysis?.matchStatus || item.effectiveStatus || 'NEEDS_REVIEW';
                items.push({
                    id: `receipt-review-${item.paymentId}`,
                    kind: 'receipt_review',
                    severity: status === 'UNREADABLE' ? 'amber' : 'red',
                    title: `${item.booking.roomName} · OCR needs review`,
                    detail: `${item.booking.customerName || 'Walk-in'} · ${status}${item.analysis?.mismatchReason ? ` · ${item.analysis.mismatchReason}` : ''}`,
                    linkHref: '/admin/receipt-analysis',
                    subjectId: item.paymentId,
                    createdAt: item.analysis?.analyzedAt || item.createdAt,
                });
            });
            if (ocrHealth && (!ocrHealth.reachable || ocrHealth.modelLoaded === false)) {
                items.push({
                    id: 'ocr-pi-health',
                    kind: ocrHealth.reachable ? 'ocr_stuck' : 'pi_unreachable',
                    severity: ocrHealth.reachable ? 'amber' : 'red',
                    title: ocrHealth.reachable ? 'OCR model loading' : 'OCR Pi offline',
                    detail: describeOcrHealth(ocrHealth),
                    linkHref: '/admin/receipt-analysis',
                    createdAt: lastOcrProbe?.toISOString() || new Date().toISOString(),
                });
            }
            activeTimeEntries.forEach((entry) => {
                const clockIn = new Date(entry.clockIn);
                const clockInVenueDate = toDateStringInTz(clockIn, VENUE_TIMEZONE);
                const ageHours = (now - clockIn.getTime()) / 3600000;
                if (clockInVenueDate === venueToday && ageHours < 12)
                    return;
                items.push({
                    id: `clock-open-${entry.id}`,
                    kind: 'clock_open_overnight',
                    severity: 'red',
                    title: `${entry.employee.name} still clocked in`,
                    detail: `Open since ${formatVenueTime(entry.clockIn)}${ageHours >= 12 ? ` · ${Math.floor(ageHours)}h` : ''}`,
                    linkHref: '/pos/time-management',
                    subjectId: entry.id,
                    createdAt: entry.clockIn,
                });
            });
        }
        bookings.forEach((booking) => {
            const status = (booking.bookingStatus || booking.status || '').toUpperCase();
            const paymentStatus = (booking.paymentStatus || '').toUpperCase();
            const isQuickSale = booking.bookingSource === 'QUICK_SALE';
            const end = new Date(booking.endTime).getTime();
            if (isQuickSale ||
                status === 'CANCELLED' ||
                status === 'COMPLETED' ||
                paymentStatus === 'PAID' ||
                Number.isNaN(end) ||
                now - end < 15 * 60000) {
                return;
            }
            items.push({
                id: `unpaid-past-end-${booking.id}`,
                kind: 'unpaid_past_end',
                severity: 'red',
                title: `${booking.roomName} · unpaid after session`,
                detail: `${booking.customerName || 'Walk-in'} · ended ${formatVenueTime(booking.endTime)}`,
                linkHref: `/pos/booking/${booking.id}`,
                subjectId: booking.id,
                createdAt: booking.endTime,
            });
        });
        return items.sort((a, b) => {
            if (a.severity !== b.severity)
                return a.severity === 'red' ? -1 : 1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    }, [
        activeTimeEntries,
        bookings,
        currentTime,
        isAdmin,
        lastOcrProbe,
        ocrHealth,
        pendingReceipts,
        receiptReviewItems,
    ]);
    useEffect(() => {
        setAttentionReadIds((prev) => {
            if (prev.size === 0)
                return prev;
            const activeIds = new Set(attentionItems.map((item) => item.id));
            const next = new Set([...prev].filter((id) => activeIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [attentionItems]);
    const markAttentionRead = useCallback((id) => {
        setAttentionReadIds((prev) => {
            if (prev.has(id))
                return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);
    const markAllAttentionRead = useCallback(() => {
        setAttentionReadIds(new Set(attentionItems.map((item) => item.id)));
    }, [attentionItems]);
    return {
        attentionItems,
        attentionReadIds,
        markAttentionRead,
        markAllAttentionRead,
        pendingReceiptsCount: pendingReceipts.length,
        receiptReviewCount: receiptSummary?.needsReview ?? receiptReviewItems.length,
        ocrHealth,
        lastOcrProbe,
        streamEvents,
    };
}
